// Core packages
import { Injectable } from '@angular/core';

// Third party packages
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Custom packages
import { TranslateService } from '@ngx-translate/core';
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { ApiService } from 'src/app/shared/services/api.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class InterventionsService {
  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private translateService: TranslateService,
  ) { }

  /**
   * Create a new intervention
   *
   * @since 1.0.0
   *
   * @param intervention Intervention data
   * @returns Observable<any>
   */
  create(intervention: any): Observable<any> {
    return this.apiService.apiCall('POST', 'interventions', intervention);
  }

  /**
   * Add a contractor to intervention contractors
   *
   * @since 1.0.0
   * @returns Observable<any>
   */
  createContractor(interventionId: string, contractor: any): Observable<any> {
    return this.apiService.apiCall('POST', `interventions/${interventionId}/contractors`, contractor);
  }

  /**
   * Add a taxDeductionFee to intervention taxDeductionFees
   *
   * @since 1.0.0
   * @returns Observable<any>
   */
  createTaxDeductionFee(interventionId: string, taxDeductionFee: any): Observable<any> {
    return this.apiService.apiCall('POST', `interventions/${interventionId}/taxDeductionFees`, taxDeductionFee);
  }

  /**
   * Add a receivedDocument to intervention receivedDocuments
   *
   * @since 1.0.0
   * @returns Observable<any>
   */
  createReceivedDocument(interventionId: string, receivedDocument: any): Observable<any> {
    return this.apiService.apiCall('POST', `interventions/${interventionId}/receivedDocuments`, receivedDocument);
  }

  /**
   * Get interventions list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', 'interventions', params);
  }

  // /**
  //  * Get interventions list for given query params
  //  *
  //  * @since 1.0.0
  //  *
  //  * @param [start] Query left offset
  //  * @param [limit] Results number
  //  * @param [sort] Sorting field name
  //  * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
  //  * @param [search] Filtering object with fildering data string
  //  */
  // getPropertyList(propertyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
  //   const params: any = { start, limit, sort, sortVersus };
  //   if (search) {
  //     search = {
  //       ...search,
  //       propertyId
  //     };
  //     Object.keys(search).forEach(key => {
  //       const newParamKey = `search[${key}]`;
  //       const newParamValue = search[key];
  //       params[newParamKey] = newParamValue;
  //     });
  //   }
  //   return this.apiService.apiCall('GET', 'interventions', params);
  // }

  // /**
  //  * Get interventions list for given query params
  //  *
  //  * @since 1.0.0
  //  *
  //  * @param [start] Query left offset
  //  * @param [limit] Results number
  //  * @param [sort] Sorting field name
  //  * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
  //  * @param [search] Filtering object with fildering data string
  //  */
  // getCompanyList(companyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
  //   const params: any = { start, limit, sort, sortVersus };
  //   if (search) {
  //     search = {
  //       ...search,
  //       companyId
  //     };
  //     Object.keys(search).forEach(key => {
  //       const newParamKey = `search[${key}]`;
  //       const newParamValue = search[key];
  //       params[newParamKey] = newParamValue;
  //     });
  //   }
  //   return this.apiService.apiCall('GET', 'interventions', params);
  // }

  /**
   * Get an exportation file of all interventions
   *
   * @since 1.0.0
   */
  getExport(): Observable<any> {
    return this.apiService.apiCall('GET', 'interventions/export', {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get given interventionId data from back-end
   *
   * @since 1.0.0
   *
   * @param interventionId ID of the intervention to get
   */
  get(interventionId: string): Observable<any> {
    return this.apiService.apiCall('GET', `interventions/${interventionId}`).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Update intervention with given interventionId with given intervention data
   *
   * @since 1.0.0
   *
   * @param interventionId ID of intervention to update
   * @param data New intervention's data
   */
  update(interventionId: string, data: object): Observable<any> {
    return this.apiService.apiCall('PUT', `interventions/${interventionId}`, data);
  }

  /**
   * Update intervention with given interventionId with given intervention data
   *
   * @since 1.0.0
   *
   * @param interventionId ID of intervention to update
   * @param contractorId ID of contractor to update
   * @param data New contractor's data
   */
  updateContractor(interventionId: string, contractorId: string, data: Object): Observable<any> {
    return this.apiService.apiCall('PUT', `interventions/${interventionId}/contractors/${contractorId}`, data).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Update intervention with given interventionId with given intervention data
   *
   * @since 1.0.0
   *
   * @param interventionId ID of intervention to update
   * @param taxDeductionFeeId ID of taxDeductionFee to update
   * @param data New taxDeductionFee's data
   */
  updateTaxDeductionFee(interventionId: string, taxDeductionFeeId: string, data: Object): Observable<any> {
    return this.apiService.apiCall('PUT', `interventions/${interventionId}/taxDeductionFees/${taxDeductionFeeId}`, data).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Update intervention with given interventionId with given intervention data
   *
   * @since 1.0.0
   *
   * @param interventionId ID of intervention to update
   * @param receivedDocumentId ID of receivedDocument to update
   * @param data New receivedDocument's data
   */
  updateReceivedDocument(interventionId: string, receivedDocumentId: string, data: Object): Observable<any> {
    return this.apiService.apiCall('PUT', `interventions/${interventionId}/receivedDocuments/${receivedDocumentId}`, data).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Update intervention status
   *
   * @since 1.0.0
   *
   * @param interventionId ID of intervention to update
   * @param status New intervention's data
   */
  changeStatus(interventionId: string, data: any): Observable<any> {
    return this.apiService.apiCall('PATCH', `interventions/${interventionId}`, data);
  }

  /**
   * Delete permanently a intervention
   *
   * @since 1.0.0
   *
   * @param interventionId ID of the intervention
   */
  delete(interventionId: string): Observable<any> {
    return this.apiService.apiCall('DELETE', `interventions/${interventionId}`);
  }
}
